import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAccessToken, logOut } from "../../../services/token";

const StaffStatus = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const abortControllerRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const connect = async () => {
      try {
        const token = await getAccessToken();

        if (!token) {
          logOut();
          return;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/messages/online-users-stream`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 401) {
            logOut();
            return;
          }
          throw new Error(`Stream request failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (isMounted) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE messages are separated by double newlines
          const parts = buffer.split("\n\n");
          buffer = parts.pop(); // keep incomplete chunk in buffer

          for (const part of parts) {
            const lines = part.split("\n");
            let eventName = "message";
            let data = "";

            for (const line of lines) {
              if (line.startsWith("event:")) {
                eventName = line.replace("event:", "").trim();
              } else if (line.startsWith("data:")) {
                data += line.replace("data:", "").trim();
              }
            }

            if (eventName === "online-users" && data) {
              try {
                const parsed = JSON.parse(data);
                if (isMounted) {
                  setStaff(parsed);
                  setLoading(false);
                }
              } catch (e) {
                console.error("Failed to parse SSE data:", e);
              }
            }
          }
        }
      } catch (err) {
        if (err.name === "AbortError") return; // expected on cleanup/unmount

        console.error("SSE stream error:", err);
        if (isMounted) setLoading(false);

        // retry after delay
        retryTimeoutRef.current = setTimeout(() => {
          if (isMounted) connect();
        }, 3000);
      }
    };

    connect();

    return () => {
      isMounted = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  const onlineStaff = useMemo(() => staff.filter((s) => s.status === "online"), [staff]);
  const offlineStaff = useMemo(() => staff.filter((s) => s.status !== "online"), [staff]);

  const getInitials = (name = "") =>
    name
      .trim()
      .split(/\s+/)
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const StaffCard = ({ user }) => {
    const isOnline = user.status === "online";

    return (
      <div className={`rounded-lg border bg-white shadow-sm hover:shadow-md transition-shadow p-4 border-l-4 ${isOnline ? "border-l-emerald-500 border-gray-200" : "border-l-gray-400 border-gray-200"}`}>
        <div className="flex items-center mb-3">
          <div className="relative flex-shrink-0 mr-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm ${isOnline ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}>{getInitials(user.staff_name)}</div>
            <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${isOnline ? "bg-emerald-500" : "bg-gray-400"}`} />
          </div>

          <div className="flex-1 min-w-0">
            <h6 className="font-medium text-gray-900 truncate">{user.staff_name}</h6>
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isOnline ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? "bg-emerald-500" : "bg-gray-400"}`} />
              {isOnline ? "Online" : "Offline"}
            </span>
          </div>
        </div>

        <div className="text-sm text-gray-500 truncate">{user.staff_email || "-"}</div>
        <div className="text-sm text-gray-500">{user.staff_mobile || "-"}</div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-300">Staff Status</h3>

        <div className="flex gap-2">
          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-1 rounded-full">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            Online: {onlineStaff.length}
          </span>
          <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-sm font-medium px-3 py-1 rounded-full">
            <span className="w-2 h-2 rounded-full bg-gray-400" />
            Offline: {offlineStaff.length}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : staff.length === 0 ? (
        <div className="text-center text-gray-400 py-20">No staff found.</div>
      ) : (
        <>
          {onlineStaff.length > 0 && (
            <div className="mb-8">
              <h6 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-600 mb-3">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Online ({onlineStaff.length})
              </h6>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {onlineStaff.map((user) => (
                  <StaffCard user={user} key={user.id} />
                ))}
              </div>
            </div>
          )}

          {offlineStaff.length > 0 && (
            <div>
              <h6 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                Offline ({offlineStaff.length})
              </h6>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {offlineStaff.map((user) => (
                  <StaffCard user={user} key={user.id} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StaffStatus;
